import type { MenuScope } from '../../menu.js';
import type { DocumentPermissions, DocumentSource } from './source.js';
import type {
  LibraryDocument,
  LibraryFolder,
  LibraryTree,
  ShipUploadsView,
  VesselFileUpload,
} from './types.js';

/**
 * Gemi kütüphanesinin ekran kuralları (plan M8a4 Task 31; ADR 0036, ADR 0038 §1, §9, §10, §12,
 * §14). Gemide ISM Forms ve Manuals ağacında geminin kendi belgeleri ve şirket belgeleri birlikte
 * durur; şirket belgesi gemide salt okunur ve "Company" rozetlidir. Ofis her geminin kütüphanesini
 * salt okunur görür (kütüphane seçici).
 */

// ---------------------------------------------------------------------------------------------
// Sahiplik ve yazma
// ---------------------------------------------------------------------------------------------

/**
 * Birleşik görünüm mü: gemide ağaç ya da ofiste bir geminin kütüphanesi (şirket ve gemi belgeleri
 * birlikte). Ofisin şirket kütüphanesinde yalnız şirket belgeleri vardır; rozet çizilmez.
 */
export function unifiedLibrary(scope: MenuScope, tree: Pick<LibraryTree, 'readOnly'> | null) {
  return scope === 'ship' || tree?.readOnly === true;
}

/** Belgenin sahip rozeti: birleşik görünümde şirket belgesi "Company" (ADR 0036 §6). */
export function ownerBadge(
  document: Pick<LibraryDocument, 'owner'>,
  unified: boolean,
): 'company' | null {
  return unified && document.owner !== 'vessel' ? 'company' : null;
}

/** Gemide yazma bağlamı: gemi kütüphanesinin işlemleri var ve kütüphane salt okunur değil. */
export interface WriteContext {
  scope: MenuScope;
  /** Web ofiste şirket kütüphanesinin işlemleri (`source.office`). */
  office: boolean;
  /** Gemide gemi kütüphanesinin işlemleri (`source.shipLibrary`). */
  shipLibrary: boolean;
  /** Ofiste bir geminin kütüphanesi: salt okunur (ADR 0038 §1). */
  readOnly: boolean;
}

/**
 * Belgeye yeni revizyon yüklenebilir mi (başlık kartındaki "New revision"): yalnız web ofiste
 * şirket belgesi; `documents.create` ister. Gemide hiçbir belgede yoktur: ISM formu, manuel ve
 * prosedür dahil dokümanı yalnız şirket revize eder ve yayınlar (sahip kararı 2026-10-18; ADR
 * 0081) [ISM 11.1, 11.2.2]. Gemi belgesi ofiste salt okunurdur [sorular #152].
 */
export function canEditDocument(
  context: WriteContext,
  document: Pick<LibraryDocument, 'owner' | 'section'>,
  permissions: Pick<DocumentPermissions, 'create'>,
): boolean {
  if (context.readOnly || !permissions.create) return false;
  return context.scope === 'web' && context.office && document.owner !== 'vessel';
}

/** Yeni belge açılabilecek klasörler: canlı ve gerçek klasörler (emekli ve gösterim grubu değil). */
export function writableFolders(folders: readonly LibraryFolder[]): LibraryFolder[] {
  return folders.filter((folder) => !folder.retired && !folder.group);
}

/**
 * "New Document" çizilir mi: yalnız ofiste şirket kütüphanesinde (`documents.create`). Gemide
 * hiçbir bölümde yoktur: dokümanı yalnız şirket ekler (sahip kararı 2026-10-18; ADR 0081; ISM
 * Forms için önce ADR 0073 §2). Ağaç imzada kalır (çağıranlar değişmez).
 */
export function canCreateDocument(
  context: WriteContext,
  _tree: Pick<LibraryTree, 'folders'> | null,
  permissions: Pick<DocumentPermissions, 'create'>,
): boolean {
  if (context.readOnly || !permissions.create) return false;
  return context.scope === 'web' && context.office;
}

/** Belgenin klasörü emekli mi (yeni revizyon ve onaylar yasak; `FOLDER_RETIRED`). */
export function inRetiredFolder(
  document: Pick<LibraryDocument, 'folderId'>,
  folders: readonly LibraryFolder[],
): boolean {
  return folders.some((folder) => folder.id === document.folderId && folder.retired === true);
}

/** Ofiste gemi kütüphanesinin yetkileri: görme ve indirme kalır, yazma yok (ADR 0038 §1). */
export function readOnlyPermissions(permissions: DocumentPermissions): DocumentPermissions {
  return {
    ...permissions,
    create: false,
    edit: false,
    approve: false,
    submit: false,
    return: false,
    void: false,
    acknowledge: false,
    manageDownloads: false,
    fileSettings: false,
    tree: false,
  };
}

// ---------------------------------------------------------------------------------------------
// Dosya durumu (ADR 0038 §14)
// ---------------------------------------------------------------------------------------------

/** Ofiste dosya durumunun metni (`documents.library.upload.<key>`) ve değerleri. */
export interface UploadLabel {
  key: string;
  tone: 'success' | 'info' | 'warning' | 'danger' | 'neutral';
  values: Record<string, number>;
}

const WAIT_KEYS: Readonly<Record<string, string>> = {
  window: 'waitingWindow',
  cap: 'waitingCap',
  paused: 'waitingPaused',
  manual: 'waitingManual',
  blocked: 'waitingBlocked',
  offline: 'onVessel',
};

/**
 * Gemi belgesinin dosyası ofiste nerede: "In office" (indirilebilir), "On vessel – uploading 42%",
 * "On vessel – waiting for upload window", "Upload failed", USB'li gemide "On vessel". Yüzde
 * bulutun aldığı bayttan (pakete ilerleme yazılmaz; sorular #164).
 */
export function uploadLabel(upload: VesselFileUpload | null | undefined): UploadLabel {
  if (!upload) return { key: 'waiting', tone: 'neutral', values: {} };
  switch (upload.state) {
    case 'available':
      return { key: 'available', tone: 'success', values: {} };
    case 'uploading': {
      const percent =
        upload.size > 0 ? Math.min(99, Math.floor((upload.bytesReceived / upload.size) * 100)) : 0;
      return { key: 'uploading', tone: 'info', values: { percent } };
    }
    case 'verifying':
      return { key: 'verifying', tone: 'info', values: {} };
    case 'on_vessel':
      return { key: 'onVessel', tone: 'neutral', values: {} };
    case 'failed':
      return { key: 'failed', tone: 'danger', values: {} };
    default: {
      const key = (upload.reason && WAIT_KEYS[upload.reason]) || 'waiting';
      return { key, tone: key === 'waitingBlocked' ? 'warning' : 'neutral', values: {} };
    }
  }
}

/** Gemide revizyon satırının yükleme işareti: kuyrukta değilse ofiste doğrulanmıştır. */
export type ShipUploadMarker = 'waiting' | 'uploading' | 'failed' | 'inOffice';

export function shipUploadMarker(
  view: ShipUploadsView | null | undefined,
  sha256: string,
): ShipUploadMarker | null {
  if (!view) return null;
  const item = view.items.find((i) => i.sha256 === sha256);
  if (!item) return 'inOffice';
  if (item.state === 'failed') return 'failed';
  if (item.state === 'uploading' || item.bytesSent > 0) return 'uploading';
  return 'waiting';
}

// ---------------------------------------------------------------------------------------------
// Web ofiste kütüphane seçici
// ---------------------------------------------------------------------------------------------

/** Seçicinin şirket değeri (`?library=company`). */
export const COMPANY_DOCUMENTS = 'company';
const STORAGE_KEY = 'felix.documents.library.v2';

/**
 * Açılan kütüphane: adresteki `?library=`; adreste yalnız `?doc=` varsa şirket kütüphanesi
 * (pano, dağıtım ve ilgili dokümanlar şirket belgesine bağlanır); sonra kişinin son seçimi; yoksa
 * şirket dokümanları. Revizyon şirket dokümanlarında girilir; sayfa ilk gemiye açılınca ortak
 * revizyonun yerini bulamadı (ADR 0081). Eski anahtardaki seçim bir kez unutulur (`STORAGE_KEY`).
 */
export function chooseLibrary(
  params: URLSearchParams,
  remembered: string | null,
  vessels: readonly { id: string }[],
): string {
  const known = (value: string | null) =>
    value === COMPANY_DOCUMENTS || vessels.some((v) => v.id === value);
  const asked = params.get('library');
  if (asked && known(asked)) return asked;
  if (params.get('doc')) return COMPANY_DOCUMENTS;
  if (remembered && known(remembered)) return remembered;
  return COMPANY_DOCUMENTS;
}

/** Kişinin son seçimi (yalnız bu tarayıcıda; depo kapalıysa yok sayılır). */
export function recallLibrary(userId: string | undefined): string | null {
  try {
    return globalThis.localStorage?.getItem(`${STORAGE_KEY}.${userId ?? 'anonymous'}`) ?? null;
  } catch {
    return null;
  }
}

export function rememberLibrary(userId: string | undefined, value: string): void {
  try {
    globalThis.localStorage?.setItem(`${STORAGE_KEY}.${userId ?? 'anonymous'}`, value);
  } catch {
    // Depo kapalı (gizli pencere): seçim yalnız adreste kalır
  }
}

/**
 * Ofiste bir geminin kütüphanesi (`/api/office/vessels/:vesselId/library/*`): ağaç gemideki
 * gibidir (geminin belgeleri ve o gemide görünen şirket belgeleri). Gemi belgesinin detayı ve
 * dosyası gemi kütüphanesi uçlarından, şirket belgesininki şirket uçlarından okunur; hangisi
 * olduğu ağaçtaki `owner`'dan bilinir. Yazma işlemleri (yükleme, onay, dağıtım) yoktur ve
 * yetkiler salt okunurdur. Favori ofiste gemi kütüphanesinde tutulmaz.
 */
export function vesselLibrarySource(base: DocumentSource, vesselId: string): DocumentSource {
  const library = base.office?.vesselLibrary;
  if (!library) throw new Error('vessel library unavailable');
  const { office: _office, ...rest } = base;
  const vesselDocuments = new Set<string>();
  const companyDocuments = new Set<string>();
  const vesselFiles = new Set<string>();
  const vesselDetail = async (documentId: string) => {
    const detail = await library.detail(vesselId, documentId);
    vesselDocuments.add(documentId);
    for (const revision of detail.revisions) {
      vesselFiles.add(revision.file.id);
      for (const attachment of revision.attachments) vesselFiles.add(attachment.id);
    }
    return detail;
  };
  return {
    ...rest,
    permissions: async () => readOnlyPermissions(await base.permissions()),
    tree: async (section) => {
      const tree = await library.tree(vesselId, section);
      for (const document of tree.documents) {
        (document.owner === 'vessel' ? vesselDocuments : companyDocuments).add(document.id);
      }
      return tree;
    },
    // Adresle açılan belge ağaçtan önce istenebilir: sahibi bilinmiyorsa önce gemi kütüphanesi,
    // orada yoksa (`NOT_FOUND`) şirket belgesi
    detail: async (documentId) => {
      if (vesselDocuments.has(documentId)) return vesselDetail(documentId);
      if (companyDocuments.has(documentId)) return base.detail(documentId);
      try {
        return await vesselDetail(documentId);
      } catch (error) {
        if ((error as { code?: unknown }).code !== 'NOT_FOUND') throw error;
        companyDocuments.add(documentId);
        return base.detail(documentId);
      }
    },
    fileUrl: (fileId, purpose) =>
      vesselFiles.has(fileId) ? library.fileUrl(vesselId, fileId) : base.fileUrl(fileId, purpose),
    downloadFile: (file) =>
      vesselFiles.has(file.fileId) ? library.download(vesselId, file) : base.downloadFile(file),
    setFavorite: async () => {
      throw Object.assign(new Error('READ_ONLY'), { code: 'READ_ONLY' });
    },
  };
}
